//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/core/services/service';
import ScreenModel from '../models/screen.model';

/**
 * @service ScreenService
 * @description Service for managing Screen definitions within the application.
 * Screens represent the various user interface views or pages available in the system.
 *
 * This service provides the interface to retrieve the master list of screens from the backend.
 * Screen definitions are often used for:
 * 1. **Access Control (ACL)**: Defining which roles have access to which screens.
 * 2. **Navigation**: Building dynamic menus based on available screens.
 * 3. **Feature Configuration**: Toggling features associated with specific screens.
 *
 * The service interacts with the `/screen/` backend endpoint.
 *
 * @feature 1. Retrieve all defined Screens.
 * @feature 2. Support for large datasets (pageSize=50000) to ensure all screens are loaded.
 * @feature 3. Robust management of concurrent HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends base Service<ScreenModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /screen/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class ScreenService extends Service<ScreenModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/screen/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/screen/');
  }

  /**
   * @method getAllScreens
   * @async
   * @description Fetch all Screen records from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a very large number of records (pageSize=50000) to ensure the complete list of screens is loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<ScreenModel>>} Promise resolving to an array of ScreenModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * screenService.getAllScreens().then(screens => {
   *   console.log('Application Screens:', screens);
   * });
   */
  public getAllScreens() {
    return new Promise<Array<ScreenModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<ScreenModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=50000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void { 
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
